import React, { useState } from "react";
import { POLICY_HINT, checkPassword } from "../utils/passwordPolicy";

export interface ChangePasswordModalProps {
  endpoint: string; // e.g. "/api/v1/auth/change-password"
  apiClient: { post: (url: string, data: any) => Promise<any> };
  forced?: boolean; // when true, no Cancel button
  title?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ChangePasswordModal({
  endpoint,
  apiClient,
  forced = false,
  title = "Change Password",
  onClose,
  onSuccess,
}: ChangePasswordModalProps) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const problem = checkPassword(newPw);
    if (problem) {
      setError(problem);
      return;
    }
    if (newPw !== confirmPw) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (newPw === currentPw) {
      setError("New password must be different from the current one.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(endpoint, { current_password: currentPw, new_password: newPw });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">{title}</h2>
        {forced && (
          <p className="text-xs text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/40 px-3 py-2 rounded mb-3">
            An administrator has reset your password. Please pick a new one before continuing.
          </p>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Current password</label>
            <input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              autoFocus
              className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">New password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={10}
              className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{POLICY_HINT}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Confirm new password</label>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          {error && <p className="text-sm text-red-500 dark:text-red-300 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            {!forced && (
              <button
                type="button"
                onClick={onClose}
                className="border dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 text-sm px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {saving ? "Saving..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
